import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import About from './components/About';
import Services from './components/Services';
import EventsShowcase from './components/EventsShowcase';
import MarketingCampaigns from './components/MarketingCampaigns';
import Contact from './components/Contact';
import Footer from './components/Footer';

function CoverPage({ onEnter }: { onEnter: () => void }) {
  return (
    <motion.div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 1.05 }}
      transition={{ duration: 1.2, ease: 'easeInOut' }}
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/e3ec006fad9a59efd0f6862911082b69.jpg"
          alt="Big Finance Cover"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center text-center px-6">

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6 }}
          className="font-serif text-6xl md:text-8xl lg:text-[110px] font-black text-white tracking-widest leading-none mb-4 uppercase"
          style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '0.12em' }}
        >
          BIG FINANCE
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="text-white/70 text-xl md:text-2xl tracking-[0.4em] uppercase font-light mb-4"
        >
          S.R.L.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="w-32 h-0.5 bg-primary mb-8"
        ></motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.4 }}
          className="text-white/60 text-base md:text-lg tracking-widest uppercase font-light mb-14"
        >
          Eventi · Marketing · Motorsport
        </motion.p>

        {/* CTA Button */}
        <motion.button
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.7 }}
          onClick={onEnter}
          className="group relative overflow-hidden bg-primary text-white px-12 py-5 rounded-full font-semibold text-base tracking-widest uppercase hover:bg-primary/90 transition-all flex items-center gap-3 whitespace-nowrap cursor-pointer"
        >
          <span>Clicca ed Esplora</span>
          <motion.i
            className="ri-arrow-right-line text-lg"
            animate={{ x: [0, 5, 0] }}
            transition={{ duration: 1.2, repeat: Infinity }}
          ></motion.i>
        </motion.button>
      </div>

      {/* Bottom tagline */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2.2 }}
        className="absolute bottom-8 text-white/40 text-xs tracking-widest uppercase"
      >
        Roma · Italia · Europa
      </motion.div>
    </motion.div>
  );
}

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [showCover, setShowCover] = useState(true);
  const [siteVisible, setSiteVisible] = useState(false);

  useEffect(() => {
    // Schema.org JSON-LD structured data
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schemaData = [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Big Finance S.R.L.',
        url: siteUrl,
        logo: 'https://static.readdy.ai/image/b4340cdd1673521bdb15ca49ead9cb84/ee7f8ad96bfd3e00e8d38ee5be667e20.png',
        description:
          'Big Finance Srl è una società innovativa specializzata in organizzazione eventi corporate, marketing strategico, motorsport di alto livello e campagne pubblicitarie. Sede a Roma, operativa in tutta Italia ed Europa.',
        email: 'bigfinancesrl@gmail.com',
        vatID: 'IT-06146231003',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Via Antonio Chinotto 1',
          addressLocality: 'Roma',
          addressRegion: 'RM',
          postalCode: '00195',
          addressCountry: 'IT',
        },
        areaServed: ['IT', 'EU'],
        sameAs: [],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: 'Big Finance S.R.L.',
        image: 'https://static.readdy.ai/image/b4340cdd1673521bdb15ca49ead9cb84/ee7f8ad96bfd3e00e8d38ee5be667e20.png',
        url: siteUrl,
        telephone: '',
        email: 'bigfinancesrl@gmail.com',
        priceRange: '€€€',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Via Antonio Chinotto 1',
          addressLocality: 'Roma',
          addressRegion: 'RM',
          postalCode: '00195',
          addressCountry: 'IT',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 41.9028,
          longitude: 12.4964,
        },
        hasMap: 'https://maps.google.com/?q=Via+Antonio+Chinotto+1,+00195+Roma+RM',
        description:
          'Organizzazione eventi corporate, marketing strategico e motorsport di livello mondiale. Sede a Roma, operiamo in tutta Italia ed Europa.',
        servesCuisine: undefined,
        serviceArea: {
          '@type': 'GeoCircle',
          geoMidpoint: {
            '@type': 'GeoCoordinates',
            latitude: 41.9028,
            longitude: 12.4964,
          },
          geoRadius: '2000000',
        },
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Servizi Big Finance',
          itemListElement: [
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Organizzazione Eventi' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Campagne di Marketing' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Ricerche di Mercato' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Eventi Fieristici e Catering' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Motorsport e Eventi Top Level' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Campagne Pubblicitarie' } },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Big Finance S.R.L.',
        url: siteUrl,
        description:
          'Sito ufficiale di Big Finance S.R.L. - Organizzazione eventi, marketing strategico e motorsport di alto livello a Roma.',
        inLanguage: 'it-IT',
        publisher: {
          '@type': 'Organization',
          name: 'Big Finance S.R.L.',
        },
      },
    ];

    const existingScript = document.getElementById('schema-org-jsonld');
    if (existingScript) existingScript.remove();

    const script = document.createElement('script');
    script.id = 'schema-org-jsonld';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('schema-org-jsonld');
      if (s) s.remove();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleEnter = () => {
    setSiteVisible(true);
    setTimeout(() => setShowCover(false), 1200);
  };

  return (
    <>
      <AnimatePresence>
        {showCover && <CoverPage onEnter={handleEnter} />}
      </AnimatePresence>

      {siteVisible && (
        <motion.div
          className="min-h-screen bg-white"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <Navbar scrolled={scrolled} />
          <Hero />
          <About />
          <Services />
          <EventsShowcase />
          <MarketingCampaigns />
          <Contact />
          <Footer />
        </motion.div>
      )}
    </>
  );
}
